import { useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import styles from './InlineTextField.module.scss'

export interface InlineTextFieldProps {
  initialValue: string
  placeholder?: string
  multiline?: boolean
  allowNewline?: boolean
  maxLength?: number
  ariaLabel: string
  /** Typography preset matching the read-only rendering the field replaces. */
  variant?: 'text' | 'note'
  fieldRef?: React.MutableRefObject<HTMLTextAreaElement | null>
  onCommit: (value: string) => void
  onCancel: () => void
  /** Shift+Enter, or Enter with splitOnEnter. Reads the caret from fieldRef; returns whether it split. */
  onSplitRequest?: () => boolean
  splitOnEnter?: boolean
  /** Caret placed on mount; defaults to the browser's autofocus position. */
  initialCaret?: number
  /** Blur cancels instead of committing. */
  cancelOnBlur?: boolean
}

/** A single-purpose inline editor: Enter commits or splits, ⌘/Ctrl+Enter commits, Escape cancels, blur commits or cancels. */
export const InlineTextField = ({
  initialValue,
  placeholder,
  multiline,
  allowNewline,
  maxLength,
  ariaLabel,
  variant = 'text',
  fieldRef,
  onCommit,
  onCancel,
  onSplitRequest,
  splitOnEnter,
  initialCaret,
  cancelOnBlur,
}: InlineTextFieldProps) => {
  const [value, setValue] = useState(initialValue)
  const committedRef = useRef(false)
  const innerRef = useRef<HTMLTextAreaElement | null>(null)
  const className = clsx(styles.field, styles[variant])

  const setRefs = (element: HTMLTextAreaElement | null) => {
    innerRef.current = element
    if (fieldRef) fieldRef.current = element
  }

  useLayoutEffect(() => {
    if (initialCaret == null) return
    innerRef.current?.setSelectionRange(initialCaret, initialCaret)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: the caret is the starting position.
  }, [])

  useLayoutEffect(() => {
    const element = innerRef.current
    if (!element) return
    element.style.height = 'auto'
    if (element.scrollHeight > 0) element.style.height = `${element.scrollHeight}px`
  }, [value, multiline])

  const commit = () => {
    if (committedRef.current) return
    committedRef.current = true
    onCommit(value)
  }

  const cancel = () => {
    if (committedRef.current) return
    committedRef.current = true
    onCancel()
  }

  const onBlur = cancelOnBlur ? cancel : commit

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      cancel()
      return
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      commit()
      return
    }
    if (event.key === 'Enter' && (event.shiftKey || splitOnEnter) && onSplitRequest) {
      event.preventDefault()
      onSplitRequest()
      return
    }
    if (event.key === 'Enter' && !(allowNewline && multiline)) {
      event.preventDefault()
      commit()
    }
  }

  if (multiline) {
    return (
      <textarea
        ref={setRefs}
        className={className}
        autoFocus
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={ariaLabel}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        rows={1}
      />
    )
  }

  return (
    <input
      className={className}
      autoFocus
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      aria-label={ariaLabel}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    />
  )
}
